import React, { FC, ReactElement, useEffect, useRef, useState } from 'react'
import { Spin, Result, Button, Space, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'
import { RonSessionLauncherProps, SessionStatus } from './types'

export const SessionPoller: FC<RonSessionLauncherProps> = ({
  statusUrl,
  fallbackUrl,
  pollIntervalMs = 500,
  maxAttempts = 30
}): ReactElement => {
  const attemptsRef = useRef(0)
  const [errored, setErrored] = useState(false)

  const { data, dataUpdatedAt } = useQuery<SessionStatus>({
    queryKey: ['ronSessionStatus', statusUrl],
    queryFn: async () => {
      attemptsRef.current += 1

      try {
        return (await axios.get<SessionStatus>(statusUrl)).data
      } catch (e) {
        if (axios.isAxiosError(e) && [401, 403, 404].includes(e.response?.status ?? 0)) {
          setErrored(true)
        }

        return { status: null, url: null }
      }
    },
    enabled: !errored,
    refetchInterval: (current) => {
      if (current?.url) return false
      if (attemptsRef.current >= maxAttempts) return false

      return pollIntervalMs
    }
  })

  useEffect(() => {
    if (data?.url) {
      window.location.replace(data.url)
    } else if (data && attemptsRef.current >= maxAttempts) {
      setErrored(true)
    }
  }, [data, dataUpdatedAt, maxAttempts])

  if (errored) {
    return (
      <Result
        status="warning"
        title="This is taking longer than expected"
        subTitle="We couldn't open your RON signing session. Please return to the order and try again."
        extra={
          <Button type="primary" href={fallbackUrl}>
            Back to order
          </Button>
        }
      />
    )
  }

  return (
    <Space direction="vertical" align="center" size="middle" style={{ width: '100%', marginTop: 48 }}>
      <Spin size="large" />
      <Typography.Text>Starting your RON session…</Typography.Text>
    </Space>
  )
}
